import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IonicModule, ModalController, RadioGroupCustomEvent } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { PurchaseResult } from '@revenuecat/purchases-js';
import { Browser } from '@capacitor/browser';

import {
  PeriodDuration,
  PeriodUnit,
  PeriodUnitEnum,
  Product,
} from '#models/subscription/product.model';
import { PurchaseService } from '#services/purchase/purchase.service';
import { ImpersonationBannerComponent } from '#components/impersonation-banner/impersonation-banner.component';
import { UploadManagerComponent } from '#components/upload-manager/upload-manager.component';

@Component({
  selector: 'app-intro-subscription',
  templateUrl: './intro-subscription.component.html',
  styleUrls: [
    './intro-subscription.component.scss',
    '../../../../theme/scoped-stylesheets/intro-screen.scss',
  ],
  standalone: true,
  imports: [IonicModule, CommonModule, ImpersonationBannerComponent, UploadManagerComponent],
})
export class IntroSubscriptionComponent implements OnInit, OnDestroy {
  @Input() modalId!: string;
  @Input() products: Product[] = [];

  selectedProduct: Product | null = null;

  private subscription = new Subscription();

  constructor(
    private modalCtrl: ModalController,
    private purchaseService: PurchaseService
  ) {
    this.subscription.add(this.products$Subscriber());
  }

  async ngOnInit() {
    await this.purchaseService.getOfferings();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private products$Subscriber() {
    return this.purchaseService.products$.subscribe((products: Product[]) => {
      if (products) {
        this.products = products;
      }
    });
  }

  async onClose() {
    await this.modalCtrl.dismiss(null, 'close', this.modalId);
  }

  onSubscriptionChange(event: RadioGroupCustomEvent<string>) {
    const selectedProduct: Product | undefined = this.products.find(
      (product) => product.identifier === event.detail.value
    );

    this.selectedProduct = selectedProduct || null;
  }

  /**
   * Initiates the purchase process for the currently selected product.
   *
   * If no product is selected, the method returns immediately.
   * Otherwise, it calls the purchase service to handle the purchase of the selected product's package.
   * Upon successful purchase, the modal is dismissed with the purchase result.
   *
   * @returns {Promise<void>} A promise that resolves when the purchase process and modal dismissal are complete.
   */
  async onPurchaseProduct(): Promise<void> {
    if (!this.selectedProduct) {
      return;
    }

    await this.purchaseService.purchaseProduct(
      this.selectedProduct.rcPackage!,
      async (data: PurchaseResult | null) => {
        if (data) {
          await this.modalCtrl.dismiss(data, 'purchase', this.modalId);
        }
      }
    );
  }

  /**
   * Returns the appropriate button label for the introductory offer.
   *
   * - If there are no products, returns 'Continue'.
   * - If the first product does not have a trial, returns 'Continue'.
   * - If the first product has a trial, returns a string indicating the duration and unit of the free trial,
   *   e.g., 'Start 7 Day Free Trial'.
   *
   * @returns {string} The button label based on the product's introductory offer.
   */
  buttonIntroductoryOfferCopy(): string {
    if (this.products.length === 0) {
      return 'Continue';
    }

    if (!this.products[0].trial) {
      return 'Continue';
    }

    const introductoryOffer: PeriodDuration | null = this.products[0].trial;

    return `Start ${introductoryOffer.period.number} ${introductoryOffer.period.unit} Free Trial`;
  }

  /**
   * Returns the abbreviation for a given product period unit.
   *
   * @param period - An optional `PeriodUnit` object containing the unit to abbreviate.
   * @returns The abbreviation for the period unit, or the original unit if no abbreviation is found.
   */
  getProductUnitAbbreviation(period?: PeriodUnit): string {
    return PeriodUnitEnum[period?.unit as keyof typeof PeriodUnitEnum] || period?.unit;
  }

  async onPrivacyPolicyClick() {
    await Browser.open({ url: 'https://lyfeplum.com/privacy-policy/' });
  }
}
